import { mediaStorage } from './provider'

interface CacheEntry {
  url: string
  expiresAt: number
}

/**
 * A short-lived cache of signed URLs.
 *
 * Signed URLs are deliberately cached for less time than they are valid, and
 * the whole cache is dropped whenever the social graph changes. Removing a
 * follower has to take effect straight away; a stale URL sitting in memory
 * would quietly keep a door open. (§117)
 */
const cache = new Map<string, CacheEntry>()
const inFlight = new Map<string, Promise<string>>()

const SAFETY_MARGIN_MS = 30_000

function key(mediaId: string, variant: 'full' | 'thumb') {
  return variant + ':' + mediaId
}

export async function resolveMediaUrl(
  mediaId: string,
  variant: 'full' | 'thumb' = 'thumb',
): Promise<string> {
  const k = key(mediaId, variant)
  const hit = cache.get(k)
  if (hit && hit.expiresAt > Date.now() + SAFETY_MARGIN_MS) return hit.url

  const pending = inFlight.get(k)
  if (pending) return pending

  const request = mediaStorage
    .getViewUrl(mediaId, variant)
    .then(({ url, expiresAt }) => {
      cache.set(k, { url, expiresAt })
      return url
    })
    .finally(() => inFlight.delete(k))

  inFlight.set(k, request)
  return request
}

/** Called whenever access could have changed: follow, unfollow, block, sign-out. */
export function invalidateMediaUrls() {
  cache.clear()
  inFlight.clear()
}

export function peekMediaUrl(mediaId: string, variant: 'full' | 'thumb' = 'thumb') {
  const hit = cache.get(key(mediaId, variant))
  return hit && hit.expiresAt > Date.now() ? hit.url : null
}

/**
 * Records a URL we already hold, so the next render does not ask for one it
 * does not need. Used right after an upload, and by the development gallery.
 */
export function primeMediaUrl(
  mediaId: string,
  url: string,
  variant: 'full' | 'thumb' = 'thumb',
  ttlMs = 5 * 60_000,
) {
  cache.set(key(mediaId, variant), { url, expiresAt: Date.now() + ttlMs })
}
